import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";
import AdminSeries from "@/pages/AdminSeries";
import Analytics from "@/pages/AdminAnalytics";
import AdminSubscribers from "@/pages/AdminSubscribers";
import SiteAdminsCard from "@/components/admin/SiteAdminsCard";
import AdminOrganizations from "@/pages/AdminOrganizations";
import AdminResourceLibrary from "@/pages/AdminResourceLibrary";
import ChangePasswordDialog from "@/pages/ChangePasswordDialog";

const emptyBook = { title: "", subtitle: "", description: "", price: 0, cover_url: "", category: "Devotional", featured: false };
const emptyStudy = { title: "", scripture: "", summary: "", body: "", body_html: "", week_number: 1, publish_at: "", downloads: [] };
const emptyNl = { month: "", title: "", preview: "", body: "", body_html: "" };
const emptyBlog = { title: "", excerpt: "", body: "", body_html: "", author: "cody", cover_url: "", publish_at: "" };
const emptyReading = { day_number: "", date: new Date().toISOString().slice(0, 10), passage: "", title: "", key_scripture: "", verse_notes: [], reflection: "" };

function Section({ title, children, onAdd }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>{title}</h2>
        <button onClick={onAdd} data-testid={`admin-add-${title.toLowerCase().replace(/\s+/g, '-')}-button`} className="btn-primary">
          <Plus size={14} /> Add new
        </button>
      </div>
      {children}
    </div>
  );
}

function Row({ children, onEdit, onDelete, testid }) {
  return (
    <div data-testid={testid} className="card-surface p-5 flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex-1 min-w-0">{children}</div>
      <div className="flex gap-2 shrink-0">
        <button onClick={onEdit} className="btn-ghost" data-testid={`${testid}-edit`}><Pencil size={14} /> Edit</button>
        <button onClick={onDelete} className="btn-ghost" data-testid={`${testid}-delete`} style={{ color: "var(--terracotta)", borderColor: "var(--terracotta)" }}>
          <Trash2 size={14} /> Delete
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--olive)" }}>{label}</Label>
      {children}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [books, setBooks] = useState([]);
  const [studies, setStudies] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [individualInquiries, setIndividualInquiries] = useState([]);
  const [churchInquiries, setChurchInquiries] = useState([]);
  const [readings, setReadings] = useState([]);

  // Modal state
  const [modal, setModal] = useState(null); // { kind: 'book'|'study'|'nl'|'blog', mode: 'create'|'edit', data }
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "analytics");

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const reload = async () => {
    const [b, s, n, bl, iq, iqInd, iqCh, rd] = await Promise.all([
      api.get("/books"),
      api.get("/admin/bible-studies"),
      api.get("/admin/newsletters"),
      api.get("/admin/blog"),
      api.get("/admin/inquiries"),
      api.get("/admin/inquiries/individual"),
      api.get("/admin/inquiries/church"),
      api.get("/admin/reading-plan"),
    ]);
    setBooks(b.data);
    setStudies(s.data);
    setNewsletters(n.data);
    setBlogPosts(bl.data);
    setInquiries(iq.data);
    setIndividualInquiries(iqInd.data);
    setChurchInquiries(iqCh.data);
    setReadings(rd.data);
  };

  const markInquiryHandled = async (id) => {
    await api.put(`/admin/inquiries/${id}`);
    toast.success("Marked handled.");
    reload();
  };
  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Deleted.");
    reload();
  };
  const markIndividualHandled = async (id) => {
    await api.put(`/admin/inquiries/individual/${id}`);
    toast.success("Marked handled.");
    reload();
  };
  const deleteIndividualInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/individual/${id}`);
    toast.success("Deleted.");
    reload();
  };
  const markChurchHandled = async (id) => {
    await api.put(`/admin/inquiries/church/${id}`);
    toast.success("Marked handled.");
    reload();
  };
  const deleteChurchInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/church/${id}`);
    toast.success("Deleted.");
    reload();
  };

  useEffect(() => {
    if (user?.is_admin) reload();
  }, [user]);

  if (loading) return <div className="px-6 py-32 text-center" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="admin-forbidden">
      <h1 className="font-serif-display text-3xl" style={{ color: "var(--ink)" }}>Not authorized</h1>
      <p className="mt-4" style={{ color: "var(--ink-2)" }}>The admin panel is for ministry staff only.</p>
    </div>
  );

  const closeModal = () => setModal(null);

  const save = async () => {
    const { kind, mode, data } = modal;
    try {
      const path = { book: "books", study: "bible-studies", nl: "newsletters", blog: "blog", reading: "reading-plan" }[kind];
      const payload = { ...data };
      if (kind === "book") payload.price = parseFloat(payload.price) || 0;
      if (kind === "study") payload.week_number = parseInt(payload.week_number) || 1;
      if (kind === "reading") {
        payload.day_number = parseInt(payload.day_number) || 0;
        payload.verse_notes = (payload.verse_notes || []).filter((v) => (v.ref || "").trim() || (v.note || "").trim());
      }
      if (mode === "create") {
        await api.post(`/admin/${path}`, payload);
        toast.success("Created.");
      } else {
        await api.put(`/admin/${path}/${data.id}`, payload);
        toast.success("Updated.");
      }
      closeModal();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    const path = { book: "books", study: "bible-studies", nl: "newsletters", blog: "blog", reading: "reading-plan" }[kind];
    try {
      await api.delete(`/admin/${path}/${id}`);
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const downloadSubscribersCsv = async () => {
    try {
      const { data } = await api.get("/admin/subscribers");
      const rows = [["email", "source", "subscribed_at"], ...data.map(s => [s.email, s.source || "", s.subscribed_at || ""])];
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} subscribers.`);
    } catch { toast.error("Export failed."); }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline mb-3">Ministry workspace</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl tracking-tight" style={{ color: "var(--ink)" }}>Admin</h1>
          <p className="mt-3" style={{ color: "var(--ink-2)" }}>Manage books, Bible studies, newsletters, and the journal.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/coord" data-testid="admin-goto-coord" className="btn-ghost">
            Open Bible Study CRM →
          </Link>
          <button onClick={downloadSubscribersCsv} data-testid="admin-export-subscribers" className="btn-ghost">
            Download subscribers (CSV)
          </button>
          <button onClick={() => setChangePwOpen(true)} data-testid="admin-change-password-btn" className="btn-ghost">
            <KeyRound size={14} /> Change password
          </button>
        </div>
      </div>

      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchParams(v === "analytics" ? {} : { tab: v }, { replace: true }); }} className="mt-10">
        <TabsList data-testid="admin-tabs" className="bg-transparent border-b w-full justify-start rounded-none gap-2 h-auto p-0 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Analytics</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Subscribers</TabsTrigger>
          <TabsTrigger value="orgs" data-testid="admin-tab-orgs" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Organizations</TabsTrigger>
          <TabsTrigger value="resources" data-testid="admin-tab-resources" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Resources</TabsTrigger>
          <TabsTrigger value="series" data-testid="admin-tab-series" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Series</TabsTrigger>
          <TabsTrigger value="books" data-testid="admin-tab-books" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Books ({books.length})</TabsTrigger>
          <TabsTrigger value="studies" data-testid="admin-tab-studies" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Bible studies ({studies.length})</TabsTrigger>
          <TabsTrigger value="newsletters" data-testid="admin-tab-newsletters" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Newsletters ({newsletters.length})</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">Journal ({blogPosts.length})</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">
            Inquiries ({inquiries.filter(i => !i.handled).length + individualInquiries.filter(i => !i.handled).length + churchInquiries.filter(i => !i.handled).length})
          </TabsTrigger>
          <TabsTrigger value="readings" data-testid="admin-tab-readings" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--terracotta)] data-[state=active]:text-[var(--ink)] px-4 py-3">
            Read With Me ({readings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-8">
          <Analytics />
        </TabsContent>

        <TabsContent value="subscribers" className="mt-8">
          <AdminSubscribers />
        </TabsContent>

        <TabsContent value="orgs" className="mt-8">
          <SiteAdminsCard />
          <AdminOrganizations />
        </TabsContent>

        <TabsContent value="resources" className="mt-8">
          <AdminResourceLibrary />
        </TabsContent>

        <TabsContent value="series" className="mt-8">
          <AdminSeries />
        </TabsContent>

        <TabsContent value="books" className="mt-8">
          <Section title="Books" onAdd={() => setModal({ kind: "book", mode: "create", data: { ...emptyBook } })}>
            <div className="space-y-3">
              {books.map((b) => (
                <Row
                  key={b.id}
                  testid={`admin-book-${b.id}`}
                  onEdit={() => setModal({ kind: "book", mode: "edit", data: { ...b } })}
                  onDelete={() => remove("book", b.id)}
                >
                  <div className="flex items-center gap-4">
                    <img src={b.cover_url} alt={b.title} className="w-14 h-20 object-cover" />
                    <div className="min-w-0">
                      <div className="overline" style={{ fontSize: "0.65rem" }}>{b.category}{b.featured && " · Featured"}</div>
                      <h3 className="font-serif-display text-lg leading-tight truncate" style={{ color: "var(--ink)" }}>{b.title}</h3>
                      <p className="text-sm" style={{ color: "var(--terracotta)" }}>${b.price.toFixed(2)}</p>
                    </div>
                  </div>
                </Row>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="studies" className="mt-8">
          <Section title="Bible studies" onAdd={() => setModal({ kind: "study", mode: "create", data: { ...emptyStudy } })}>
            <div className="space-y-3">
              {studies.map((s) => (
                <Row key={s.id} testid={`admin-study-${s.id}`}
                  onEdit={() => setModal({ kind: "study", mode: "edit", data: { ...s } })}
                  onDelete={() => remove("study", s.id)}
                >
                  <div className="overline" style={{ fontSize: "0.65rem" }}>Week {s.week_number} · {s.scripture}</div>
                  <h3 className="font-serif-display text-lg leading-tight" style={{ color: "var(--ink)" }}>{s.title}</h3>
                  <p className="text-sm truncate" style={{ color: "var(--ink-2)" }}>{s.summary}</p>
                </Row>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="newsletters" className="mt-8">
          <Section title="Newsletters" onAdd={() => setModal({ kind: "nl", mode: "create", data: { ...emptyNl } })}>
            <div className="space-y-3">
              {newsletters.map((n) => (
                <Row key={n.id} testid={`admin-nl-${n.id}`}
                  onEdit={() => setModal({ kind: "nl", mode: "edit", data: { ...n } })}
                  onDelete={() => remove("nl", n.id)}
                >
                  <div className="overline" style={{ fontSize: "0.65rem" }}>{n.month}</div>
                  <h3 className="font-serif-display text-lg leading-tight" style={{ color: "var(--ink)" }}>{n.title}</h3>
                  <p className="text-sm truncate" style={{ color: "var(--ink-2)" }}>{n.preview}</p>
                </Row>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="blog" className="mt-8">
          <Section title="Journal" onAdd={() => setModal({ kind: "blog", mode: "create", data: { ...emptyBlog } })}>
            <div className="space-y-3">
              {blogPosts.map((p) => (
                <Row key={p.id} testid={`admin-blog-${p.id}`}
                  onEdit={() => setModal({ kind: "blog", mode: "edit", data: { ...p } })}
                  onDelete={() => remove("blog", p.id)}
                >
                  <div className="overline" style={{ fontSize: "0.65rem" }}>{p.author === "cody" ? "Cody D. Thrash" : "Samantha N. Thrash"}</div>
                  <h3 className="font-serif-display text-lg leading-tight" style={{ color: "var(--ink)" }}>{p.title}</h3>
                  <p className="text-sm truncate" style={{ color: "var(--ink-2)" }}>{p.excerpt}</p>
                </Row>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="readings" className="mt-8">
          <Section title="Read With Me" onAdd={() => setModal({ kind: "reading", mode: "create", data: { ...emptyReading } })}>
            <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
              Showing all {readings.length} days. Days with a date in the future are not yet visible to the public.
            </p>
            <div className="space-y-3">
              {readings.map((r) => {
                const today = new Date().toISOString().slice(0, 10);
                const isFuture = r.date > today;
                const hasNotes = (r.verse_notes && r.verse_notes.length > 0) || (r.reflection && r.reflection.trim());
                return (
                  <Row
                    key={r.id}
                    testid={`admin-reading-${r.day_number}`}
                    onEdit={() => setModal({ kind: "reading", mode: "edit", data: { ...r, verse_notes: r.verse_notes || [] } })}
                    onDelete={() => remove("reading", r.id)}
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-center w-16 shrink-0">
                        <div className="font-serif-display text-2xl leading-none" style={{ color: "var(--ember)" }}>
                          {r.day_number}
                        </div>
                        <div className="text-[0.6rem] uppercase tracking-wider mt-1" style={{ color: "var(--muted)" }}>Day</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>{r.date}</span>
                          {isFuture && (
                            <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--bg-base)", color: "var(--muted)", border: "1px solid var(--border-subtle)" }}>
                              Future
                            </span>
                          )}
                          {hasNotes ? (
                            <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--bg-base)", color: "var(--olive)", border: "1px solid var(--olive)" }}>
                              Notes
                            </span>
                          ) : (
                            <span className="text-[0.6rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                              No notes yet
                            </span>
                          )}
                        </div>
                        <h3 className="font-serif-display-italic text-lg leading-tight" style={{ color: "var(--ink)" }}>{r.passage}</h3>
                        {r.title && <p className="text-xs italic mt-1" style={{ color: "var(--terracotta)" }}>{r.title}</p>}
                      </div>
                    </div>
                  </Row>
                );
              })}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="inquiries" className="mt-8">
          {/* Church demo requests (thedisciple.tracker) */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>
              Church demo requests
            </h2>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
              {churchInquiries.filter(i => !i.handled).length} new · {churchInquiries.length} total
            </div>
          </div>
          {churchInquiries.length === 0 ? (
            <p className="mb-12" style={{ color: "var(--muted)" }}>No church demo requests yet. Share <span style={{ color: "var(--ink-2)" }}>/resources</span> to start filling this up.</p>
          ) : (
            <div className="space-y-3 mb-12">
              {churchInquiries.map((iq) => (
                <div key={iq.id} data-testid={`admin-church-inquiry-${iq.id}`} className="card-surface p-6" style={{ borderColor: iq.handled ? "var(--border-subtle)" : "var(--ember)" }}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 text-xs uppercase tracking-[0.2em] flex-wrap">
                        <span style={{ color: iq.handled ? "var(--muted)" : "var(--ember)" }}>
                          {iq.handled ? "Handled" : "New"}
                        </span>
                        <span style={{ color: "var(--muted)" }}>{new Date(iq.created_at).toLocaleString()}</span>
                        <span className="px-2 py-0.5" style={{ background: "var(--bg-base)", color: "var(--gold)" }}>
                          thedisciple.{iq.tool}
                        </span>
                        {iq.church_size && (
                          <span className="px-2 py-0.5" style={{ background: "var(--bg-base)", color: "var(--olive)" }}>{iq.church_size}</span>
                        )}
                      </div>
                      <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>
                        {iq.church_name}
                      </h3>
                      <div className="mt-1 text-sm" style={{ color: "var(--ink-2)" }}>
                        {iq.name}{iq.role ? ` · ${iq.role}` : ""}{iq.city ? ` · ${iq.city}` : ""}
                      </div>
                      <div className="mt-2 text-sm space-y-1" style={{ color: "var(--ink-2)" }}>
                        <div>📧 <a href={`mailto:${iq.email}`} className="underline" style={{ color: "var(--ember)" }}>{iq.email}</a></div>
                        {iq.phone && <div>📱 <a href={`tel:${iq.phone}`} className="underline" style={{ color: "var(--ember)" }}>{iq.phone}</a></div>}
                      </div>
                      {iq.message && (
                        <div className="mt-4 p-4" style={{ background: "var(--bg-base)", borderLeft: "2px solid var(--terracotta)" }}>
                          <div className="text-[0.6rem] uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>Message</div>
                          <p className="text-sm whitespace-pre-line" style={{ color: "var(--ink-2)" }}>{iq.message}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {!iq.handled && (
                        <button onClick={() => markChurchHandled(iq.id)} data-testid={`admin-church-${iq.id}-handle`} className="btn-primary" style={{ padding: "0.5rem 0.9rem" }}>
                          Mark handled
                        </button>
                      )}
                      <button onClick={() => deleteChurchInquiry(iq.id)} className="btn-ghost" style={{ padding: "0.5rem 0.9rem", color: "var(--ember)", borderColor: "var(--ember)" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Individual counseling inquiries */}
          <div className="flex items-center justify-between mb-6 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>
              Individual counseling
            </h2>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
              {individualInquiries.filter(i => !i.handled).length} new · {individualInquiries.length} total
            </div>
          </div>
          {individualInquiries.length === 0 ? (
            <p className="mb-10" style={{ color: "var(--muted)" }}>No individual counseling inquiries yet.</p>
          ) : (
            <div className="space-y-3 mb-12">
              {individualInquiries.map((iq) => (
                <div key={iq.id} data-testid={`admin-ic-inquiry-${iq.id}`} className="card-surface p-6" style={{ borderColor: iq.handled ? "var(--border-subtle)" : "var(--ember)" }}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 text-xs uppercase tracking-[0.2em]">
                        <span style={{ color: iq.handled ? "var(--muted)" : "var(--ember)" }}>
                          {iq.handled ? "Handled" : "New"}
                        </span>
                        <span style={{ color: "var(--muted)" }}>{new Date(iq.created_at).toLocaleString()}</span>
                        <span className="px-2 py-0.5" style={{ background: "var(--bg-base)", color: "var(--gold)" }}>
                          {iq.package === "growth" ? "Growth $250" : "Essential $75"}
                        </span>
                      </div>
                      <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>{iq.name}</h3>
                      <div className="mt-2 text-sm space-y-1" style={{ color: "var(--ink-2)" }}>
                        <div>📧 <a href={`mailto:${iq.email}`} className="underline" style={{ color: "var(--ember)" }}>{iq.email}</a></div>
                        <div>📱 <a href={`tel:${iq.phone}`} className="underline" style={{ color: "var(--ember)" }}>{iq.phone}</a></div>
                      </div>
                      <div className="mt-4 p-4" style={{ background: "var(--bg-base)", borderLeft: "2px solid var(--terracotta)" }}>
                        <div className="text-[0.6rem] uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>Reason</div>
                        <p className="text-sm whitespace-pre-line" style={{ color: "var(--ink-2)" }}>{iq.reason}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {!iq.handled && (
                        <button onClick={() => markIndividualHandled(iq.id)} data-testid={`admin-ic-${iq.id}-handle`} className="btn-primary" style={{ padding: "0.5rem 0.9rem" }}>
                          Mark handled
                        </button>
                      )}
                      <button onClick={() => deleteIndividualInquiry(iq.id)} className="btn-ghost" style={{ padding: "0.5rem 0.9rem", color: "var(--ember)", borderColor: "var(--ember)" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Marriage inquiries */}
          <div className="flex items-center justify-between mb-6 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>
              Marriage Inquiries
            </h2>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
              {inquiries.filter(i => !i.handled).length} new · {inquiries.length} total
            </div>
          </div>
          {inquiries.length === 0 ? (
            <p style={{ color: "var(--muted)" }}>No inquiries yet. Share /counseling/before-the-vows to start filling this up.</p>
          ) : (
            <div className="space-y-3">
              {inquiries.map((iq) => (
                <div key={iq.id} data-testid={`admin-inquiry-${iq.id}`} className="card-surface p-6" style={{ borderColor: iq.handled ? "var(--border-subtle)" : "var(--ember)" }}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 text-xs uppercase tracking-[0.2em]">
                        <span style={{ color: iq.handled ? "var(--muted)" : "var(--ember)" }}>
                          {iq.handled ? "Handled" : "New"}
                        </span>
                        <span style={{ color: "var(--muted)" }}>
                          {new Date(iq.created_at).toLocaleString()}
                        </span>
                        <span className="px-2 py-0.5" style={{ background: "var(--bg-base)", color: "var(--gold)" }}>
                          {iq.service === "marriage-mentoring" ? "Marriage Mentoring" : "Before the Vows"} · {
                            iq.program === "7-week" ? "7wk $525"
                            : iq.program === "12-week" ? "12wk $900"
                            : iq.program === "per-session" ? "$100/90-min"
                            : "Not sure"
                          }
                        </span>
                      </div>
                      <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>
                        {iq.groom_name} &amp; {iq.bride_name}
                      </h3>
                      <div className="mt-2 text-sm space-y-1" style={{ color: "var(--ink-2)" }}>
                        <div>📧 <a href={`mailto:${iq.email}`} className="underline" style={{ color: "var(--ember)" }}>{iq.email}</a></div>
                        <div>📱 <a href={`tel:${iq.phone}`} className="underline" style={{ color: "var(--ember)" }}>{iq.phone}</a></div>
                        {iq.wedding_date && <div>💍 Wedding: {iq.wedding_date}</div>}
                      </div>
                      {iq.message && (
                        <p className="mt-3 text-sm italic" style={{ color: "var(--ink-2)" }}>
                          "{iq.message}"
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {!iq.handled && (
                        <button onClick={() => markInquiryHandled(iq.id)} data-testid={`admin-inquiry-${iq.id}-handle`} className="btn-primary" style={{ padding: "0.5rem 0.9rem" }}>
                          Mark handled
                        </button>
                      )}
                      <button onClick={() => deleteInquiry(iq.id)} className="btn-ghost" style={{ padding: "0.5rem 0.9rem", color: "var(--ember)", borderColor: "var(--ember)" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Editor modal */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent data-testid="admin-editor-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-base)" }}>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {modal?.mode === "create" ? "Add new" : "Edit"} {modal?.kind === "book" ? "book" : modal?.kind === "study" ? "Bible study" : modal?.kind === "nl" ? "newsletter" : modal?.kind === "reading" ? "reading day" : "journal entry"}
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "var(--muted)" }}>
              Fill in the details below. Changes publish immediately.
            </DialogDescription>
          </DialogHeader>

          {modal?.kind === "book" && (
            <div className="space-y-4">
              <Field label="Title"><Input data-testid="admin-book-title-input" value={modal.data.title} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} /></Field>
              <Field label="Subtitle"><Input data-testid="admin-book-subtitle-input" value={modal.data.subtitle || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, subtitle: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price (USD)"><Input data-testid="admin-book-price-input" type="number" step="0.01" value={modal.data.price} onChange={(e) => setModal({ ...modal, data: { ...modal.data, price: e.target.value } })} /></Field>
                <Field label="Category"><Input data-testid="admin-book-category-input" value={modal.data.category} onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })} /></Field>
              </div>
              <Field label="Cover image URL"><Input data-testid="admin-book-cover-input" value={modal.data.cover_url} onChange={(e) => setModal({ ...modal, data: { ...modal.data, cover_url: e.target.value } })} /></Field>
              <Field label="Description"><Textarea data-testid="admin-book-description-input" rows={6} value={modal.data.description} onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })} /></Field>
              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--ink-2)" }}>
                <input data-testid="admin-book-featured-toggle" type="checkbox" checked={!!modal.data.featured} onChange={(e) => setModal({ ...modal, data: { ...modal.data, featured: e.target.checked } })} />
                Feature on home page
              </label>
            </div>
          )}

          {modal?.kind === "study" && (
            <div className="space-y-4">
              <Field label="Title"><Input data-testid="admin-study-title-input" value={modal.data.title} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Scripture reference"><Input data-testid="admin-study-scripture-input" value={modal.data.scripture} onChange={(e) => setModal({ ...modal, data: { ...modal.data, scripture: e.target.value } })} /></Field>
                <Field label="Week number"><Input data-testid="admin-study-week-input" type="number" value={modal.data.week_number} onChange={(e) => setModal({ ...modal, data: { ...modal.data, week_number: e.target.value } })} /></Field>
              </div>
              <Field label="Summary"><Textarea data-testid="admin-study-summary-input" rows={2} value={modal.data.summary} onChange={(e) => setModal({ ...modal, data: { ...modal.data, summary: e.target.value } })} /></Field>
              <Field label="Body (rich text — paste from Google Docs)">
                <RichTextEditor
                  testid="admin-study-body-editor"
                  value={modal.data.body_html || ""}
                  onChange={(html) => setModal({ ...modal, data: { ...modal.data, body_html: html, body: html.replace(/<[^>]+>/g, "") } })}
                />
              </Field>
              <Field label="Downloadable files (paste links — Google Drive, Dropbox, etc.)">
                <div className="space-y-2">
                  {(modal.data.downloads || []).map((d, idx) => (
                    <div key={idx} className="flex gap-2" data-testid={`admin-study-download-row-${idx}`}>
                      <Input
                        placeholder="Label (e.g. Workbook PDF)"
                        value={d.label}
                        onChange={(e) => {
                          const downloads = [...(modal.data.downloads || [])];
                          downloads[idx] = { ...downloads[idx], label: e.target.value };
                          setModal({ ...modal, data: { ...modal.data, downloads } });
                        }}
                        data-testid={`admin-study-download-label-${idx}`}
                        className="w-1/3"
                      />
                      <Input
                        placeholder="https://..."
                        value={d.url}
                        onChange={(e) => {
                          const downloads = [...(modal.data.downloads || [])];
                          downloads[idx] = { ...downloads[idx], url: e.target.value };
                          setModal({ ...modal, data: { ...modal.data, downloads } });
                        }}
                        data-testid={`admin-study-download-url-${idx}`}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const downloads = (modal.data.downloads || []).filter((_, i) => i !== idx);
                          setModal({ ...modal, data: { ...modal.data, downloads } });
                        }}
                        data-testid={`admin-study-download-remove-${idx}`}
                        className="btn-ghost"
                        style={{ padding: "0.5rem 0.75rem" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setModal({ ...modal, data: { ...modal.data, downloads: [...(modal.data.downloads || []), { label: "", url: "" }] } })}
                    data-testid="admin-study-download-add"
                    className="btn-ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                  >
                    <Plus size={14} /> Add file
                  </button>
                </div>
              </Field>
              <Field label="Schedule publish at (ISO, optional — leave blank to publish now)">
                <Input data-testid="admin-study-publish-at-input" placeholder="e.g. 2026-03-01T08:00:00Z" value={modal.data.publish_at || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, publish_at: e.target.value } })} />
              </Field>
            </div>
          )}

          {modal?.kind === "nl" && (
            <div className="space-y-4">
              <Field label="Month"><Input data-testid="admin-nl-month-input" placeholder="e.g. February 2026" value={modal.data.month} onChange={(e) => setModal({ ...modal, data: { ...modal.data, month: e.target.value } })} /></Field>
              <Field label="Title"><Input data-testid="admin-nl-title-input" value={modal.data.title} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} /></Field>
              <Field label="Preview"><Textarea data-testid="admin-nl-preview-input" rows={2} value={modal.data.preview} onChange={(e) => setModal({ ...modal, data: { ...modal.data, preview: e.target.value } })} /></Field>
              <Field label="Letter body (rich text — paste from Google Docs)">
                <RichTextEditor
                  testid="admin-nl-body-editor"
                  value={modal.data.body_html || ""}
                  onChange={(html) => setModal({ ...modal, data: { ...modal.data, body_html: html, body: html.replace(/<[^>]+>/g, "") } })}
                />
              </Field>
            </div>
          )}

          {modal?.kind === "reading" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Day number">
                  <Input data-testid="admin-reading-day-input" type="number" value={modal.data.day_number} onChange={(e) => setModal({ ...modal, data: { ...modal.data, day_number: e.target.value } })} />
                </Field>
                <Field label="Date (when it goes live)">
                  <Input data-testid="admin-reading-date-input" type="date" value={modal.data.date} onChange={(e) => setModal({ ...modal, data: { ...modal.data, date: e.target.value } })} />
                </Field>
              </div>
              <Field label="Title (optional — shown above passage)">
                <Input data-testid="admin-reading-title-input" placeholder="e.g. He calls us up the mountain" value={modal.data.title || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} />
              </Field>
              <Field label="Passage">
                <Input data-testid="admin-reading-passage-input" placeholder="e.g. 1 Chronicles 1–5" value={modal.data.passage} onChange={(e) => setModal({ ...modal, data: { ...modal.data, passage: e.target.value } })} />
              </Field>
              <Field label="Key scripture (optional — one verse you want to highlight)">
                <Input data-testid="admin-reading-key-scripture-input" placeholder="e.g. Psalm 121:1–2" value={modal.data.key_scripture || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, key_scripture: e.target.value } })} />
              </Field>

              <Field label="Verse notes (specific verses with your thoughts)">
                <div className="space-y-3">
                  {(modal.data.verse_notes || []).map((vn, idx) => (
                    <div key={idx} className="card-surface p-4 space-y-2" data-testid={`admin-reading-verse-row-${idx}`}>
                      <div className="flex items-center justify-between gap-2">
                        <Input
                          placeholder="Verse reference (e.g. 1 Chr 4:10)"
                          value={vn.ref}
                          onChange={(e) => {
                            const verse_notes = [...(modal.data.verse_notes || [])];
                            verse_notes[idx] = { ...verse_notes[idx], ref: e.target.value };
                            setModal({ ...modal, data: { ...modal.data, verse_notes } });
                          }}
                          data-testid={`admin-reading-verse-ref-${idx}`}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const verse_notes = (modal.data.verse_notes || []).filter((_, i) => i !== idx);
                            setModal({ ...modal, data: { ...modal.data, verse_notes } });
                          }}
                          data-testid={`admin-reading-verse-remove-${idx}`}
                          className="btn-ghost"
                          style={{ padding: "0.5rem 0.75rem" }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <Textarea
                        placeholder="Your thoughts on this verse…"
                        rows={3}
                        value={vn.note}
                        onChange={(e) => {
                          const verse_notes = [...(modal.data.verse_notes || [])];
                          verse_notes[idx] = { ...verse_notes[idx], note: e.target.value };
                          setModal({ ...modal, data: { ...modal.data, verse_notes } });
                        }}
                        data-testid={`admin-reading-verse-note-${idx}`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setModal({ ...modal, data: { ...modal.data, verse_notes: [...(modal.data.verse_notes || []), { ref: "", note: "" }] } })}
                    data-testid="admin-reading-verse-add"
                    className="btn-ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                  >
                    <Plus size={14} /> Add verse note
                  </button>
                </div>
              </Field>

              <Field label="General reflection (your daily devotional — ~150–200 words)">
                <Textarea
                  data-testid="admin-reading-reflection-input"
                  rows={10}
                  placeholder="The devotional thought for this day's reading…"
                  value={modal.data.reflection || ""}
                  onChange={(e) => setModal({ ...modal, data: { ...modal.data, reflection: e.target.value } })}
                />
              </Field>
            </div>
          )}

          {modal?.kind === "blog" && (
            <div className="space-y-4">
              <Field label="Author">
                <select
                  data-testid="admin-blog-author-select"
                  value={modal.data.author}
                  onChange={(e) => setModal({ ...modal, data: { ...modal.data, author: e.target.value } })}
                  className="w-full px-4 py-3 outline-none"
                >
                  <option value="cody">Cody D. Thrash</option>
                  <option value="samantha">Samantha N. Thrash</option>
                </select>
              </Field>
              <Field label="Title"><Input data-testid="admin-blog-title-input" value={modal.data.title} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} /></Field>
              <Field label="Cover image URL (optional)"><Input data-testid="admin-blog-cover-input" value={modal.data.cover_url || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, cover_url: e.target.value } })} /></Field>
              <Field label="Excerpt"><Textarea data-testid="admin-blog-excerpt-input" rows={2} value={modal.data.excerpt} onChange={(e) => setModal({ ...modal, data: { ...modal.data, excerpt: e.target.value } })} /></Field>
              <Field label="Body (rich text — paste from Google Docs)">
                <RichTextEditor
                  testid="admin-blog-body-editor"
                  value={modal.data.body_html || ""}
                  onChange={(html) => setModal({ ...modal, data: { ...modal.data, body_html: html, body: html.replace(/<[^>]+>/g, "") } })}
                />
              </Field>
              <Field label="Schedule publish at (ISO, optional — leave blank to publish now)">
                <Input data-testid="admin-blog-publish-at-input" placeholder="e.g. 2026-03-01T08:00:00Z" value={modal.data.publish_at || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, publish_at: e.target.value } })} />
              </Field>
            </div>
          )}

          <DialogFooter>
            <button onClick={closeModal} data-testid="admin-editor-cancel" className="btn-ghost">Cancel</button>
            <button onClick={save} data-testid="admin-editor-save" className="btn-primary">
              {modal?.mode === "create" ? "Publish" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
